import { memo } from 'react';
import PropTypes from 'prop-types';
import {
  TIMELINE_CARD_VARIANTS,
  TIMELINE_VARIANT_STYLES,
  normalizeTimelineCardVariant,
} from './timelineCardClasses.js';

/**
 * Timeline Card (Figma DS AIMS OS — node 15990:8183).
 *
 * A color-coded, **read-only** card representing a moment in time or a category
 * of activity: a bold `value` (Text/[Color]), an optional `title` (Text/Body,
 * shared across variants) and a trailing swappable `icon`. Color conveys
 * category — not sequence. No hover / click / selection states.
 *
 * Height hugs content (min 40px): title-only renders a compact ~40px card, while
 * value + title stacks to ~65px. Width fills its container (scales inside
 * {@link TimelineWidget}).
 *
 * @param {object} props
 * @param {'primary'|'red'|'orange'|'green'|'purple'|'lightBlue'|'yellow'|'limeGreen'} [props.variant='primary']
 * @param {import('react').ReactNode} [props.value] - Primary bold line (keep short — e.g. a KPI, date, status).
 * @param {import('react').ReactNode} [props.title] - Secondary label line.
 * @param {import('lucide-react').LucideIcon} [props.icon] - Trailing type icon.
 */
const TimelineCard = ({ variant = 'primary', value, title, icon: Icon, className = '', ...props }) => {
  const normalizedVariant = normalizeTimelineCardVariant(variant);
  const styles = TIMELINE_VARIANT_STYLES[normalizedVariant];

  return (
    <div
      className={['flex min-h-[40px] min-w-0 items-center justify-between gap-3 rounded-2xl border p-3', styles.container, className]
        .filter(Boolean)
        .join(' ')}
      data-variant={normalizedVariant}
      {...props}
    >
      <div className="flex min-w-0 flex-col justify-center">
        {value != null && value !== '' ? (
          <span className={['truncate text-base font-semibold leading-tight', styles.text].join(' ')}>{value}</span>
        ) : null}
        {title != null && title !== '' ? (
          <span className="truncate text-sm font-medium leading-tight text-content-secondary">{title}</span>
        ) : null}
      </div>
      {Icon ? <Icon size={18} className={['shrink-0', styles.text].join(' ')} aria-hidden /> : null}
    </div>
  );
};

TimelineCard.propTypes = {
  variant: PropTypes.oneOf(TIMELINE_CARD_VARIANTS),
  value: PropTypes.node,
  title: PropTypes.node,
  icon: PropTypes.elementType,
  className: PropTypes.string,
};

export default memo(TimelineCard);
